import ColorPicker from "./ColorPicker"
import GradientPicker from "./GradientPicker"
import LogoUpload from "./LogoUpload"
import LogoSizeSelect from "./LogoSizeSelect"
import FrameSelect from "./FrameSelect"
import ShapeSelect from "./ShapeSelect"
import TextFields from "./TextFields"

// Customisation for the builder. The code already exists and works; this
// restyles it live (colors / logo / frame / text / shape). `design` / `setDesign` are
// owned by QrBuilder, which debounced-restyles the real code on every change.
//
// layout="hero": collapsed by default, one tall column (marketing ResultCard).
// layout="dashboard": open by default, a two-column group grid — Colors spans
// two rows on the left, Logo + Frame stack beside it, Text and Shape span full
// width.
export default function CustomizePanel({ design, setDesign, error, open = false, existingLogoUrl = null, canUseShapes = false, onUpgrade, layout = "hero" }) {
  const dashboard = layout === "dashboard"

  const colorsGroup = (
    <div className={dashboard ? "md:row-span-2 min-w-0" : ""}>
      <span className="block text-sm font-semibold mb-2">Colors</span>
      <ColorPicker
        foreground={design.foregroundColor}
        background={design.backgroundColor}
        onChange={({ foreground, background }) =>
          setDesign((d) => ({ ...d, foregroundColor: foreground, backgroundColor: background }))
        }
      />
      <div className="mt-3">
        <GradientPicker
          foreground={design.foregroundColor}
          background={design.backgroundColor}
          gradientColor={design.gradientColor}
          gradientType={design.gradientType}
          onChange={({ gradientColor, gradientType }) => setDesign((d) => ({ ...d, gradientColor, gradientType }))}
        />
      </div>
    </div>
  )

  // The size control only bites when a logo is actually set (a fresh upload or
  // a persisted one that hasn't been cleared), so it stays hidden otherwise.
  const hasLogo = !!(design.logo || existingLogoUrl) && !design.removeLogo

  const logoGroup = (
    <div className="min-w-0">
      <span className="block text-sm font-semibold mb-2">Logo</span>
      <LogoUpload
        file={design.logo}
        // QrBuilder drops the File once the logo is saved and keeps its name.
        fileName={design.logoName || null}
        previewUrl={design.removeLogo ? null : existingLogoUrl}
        onChange={(file) => setDesign((d) => ({ ...d, logo: file, removeLogo: false }))}
        onRemove={() => setDesign((d) => ({ ...d, logo: null, removeLogo: true }))}
      />
      {hasLogo && (
        <LogoSizeSelect
          value={design.logoSize}
          onChange={(logoSize) => setDesign((d) => ({ ...d, logoSize }))}
        />
      )}
    </div>
  )

  const frameGroup = (
    <div className="min-w-0">
      <span className="block text-sm font-semibold mb-2">Frame</span>
      <FrameSelect
        value={design.frameStyle}
        onChange={(value) => setDesign((d) => ({ ...d, frameStyle: value }))}
      />
    </div>
  )

  const textGroup = (
    <div className={dashboard ? "md:col-span-2 min-w-0" : ""}>
      <span className="block text-sm font-semibold mb-2">Text</span>
      <TextFields design={design} setDesign={setDesign} idPrefix={`qr-text-${layout}`} columns={dashboard} />
    </div>
  )

  const shapeGroup = (
    <div className={dashboard ? "md:col-span-2 min-w-0" : ""}>
      <span className="flex items-center gap-2 text-sm font-semibold mb-2">
        Shape
        {!canUseShapes && (
          <span className="inline-flex items-center rounded-full bg-plasma px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">Pro</span>
        )}
      </span>
      <ShapeSelect
        moduleShape={design.moduleShape}
        eyeShape={design.eyeShape}
        locked={!canUseShapes}
        onUpgrade={onUpgrade}
        onChange={({ moduleShape, eyeShape }) =>
          setDesign((d) => ({ ...d, moduleShape, eyeShape }))
        }
      />
    </div>
  )

  const errorEl = error && (
    <p className={"text-sm text-[color:var(--color-fail)] " + (dashboard ? "md:col-span-2 text-center" : "text-center")}>{error}</p>
  )

  const body = dashboard ? (
    <div className="px-4 pb-4 pt-3 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5 items-start">
      {colorsGroup}
      {logoGroup}
      {frameGroup}
      {textGroup}
      {shapeGroup}
      {errorEl}
    </div>
  ) : (
    <div className="px-4 pb-4 pt-1 space-y-5">
      {colorsGroup}
      {logoGroup}
      {frameGroup}
      {textGroup}
      {shapeGroup}
      {errorEl}
    </div>
  )

  return (
    <details open={open} className="group rounded-lg border border-[color:var(--color-border)] dark:border-storm overflow-hidden">
      <summary className="cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden px-4 py-3 flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-coal dark:text-cloud">Customize design</div>
          <div className="text-xs text-[color:var(--color-muted)] dark:text-muted-light">Colors, logo, frame, text, shape. All optional.</div>
        </div>
        <svg
          viewBox="0 0 24 24"
          width="18"
          height="18"
          aria-hidden="true"
          className="text-coal dark:text-cloud transition-transform group-open:rotate-180"
          fill="currentColor"
        >
          <path d="M7 10l5 5 5-5z" />
        </svg>
      </summary>
      {body}
    </details>
  )
}
